/**
 * Lightweight SEO helper — manages <title>, meta tags, canonical, and JSON-LD per route.
 * No third-party dep; works on React 19.
 */

const SITE = {
  name: "Beauty & Balance by Bella",
  url: "https://beautyandbalance.biz",
  phone: "+1-916-243-5514",
  phoneDisplay: "916-243-5514",
  email: "Bella@triplebtherapy.com",
  city: "Sacramento",
  region: "CA",
  defaultImage: "https://beautyandbalance.biz/og-default.jpg",
};

export const SITE_INFO = SITE;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const tag = document.getElementById(id);
  if (tag) tag.remove();
}

/**
 * Apply meta + canonical + JSON-LD for a page.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.path]    — path component (with leading slash)
 * @param {string} [opts.image]
 * @param {string} [opts.type]    — og:type ("website" | "article")
 * @param {object|object[]} [opts.jsonLd]
 */
export function applySeo({ title, description, path = "/", image, type = "website", jsonLd } = {}) {
  if (typeof document === "undefined") return;
  const url = `${SITE.url}${path}`;
  const img = image || SITE.defaultImage;

  if (title) document.title = title;
  setMeta("description", description);
  setMeta("og:title", title, "property");
  setMeta("og:description", description, "property");
  setMeta("og:type", type, "property");
  setMeta("og:url", url, "property");
  setMeta("og:image", img, "property");
  setMeta("og:site_name", SITE.name, "property");
  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", title);
  setMeta("twitter:description", description);
  setMeta("twitter:image", img);
  setLink("canonical", url);

  // Clear previously injected per-page JSON-LD (besides the persistent local-business node)
  removeJsonLd("ld-page");
  removeJsonLd("ld-faq");
  removeJsonLd("ld-breadcrumb");

  if (jsonLd) {
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.forEach((block, i) => {
      const id =
        block?.["@type"] === "FAQPage"
          ? "ld-faq"
          : block?.["@type"] === "BreadcrumbList"
          ? "ld-breadcrumb"
          : `ld-page${i ? "-" + i : ""}`;
      setJsonLd(id, block);
    });
  }
}

/**
 * Persistent base JSON-LD graph — mounted once at app start so every page carries it.
 * Uses @graph to link LocalBusiness ↔ Organization ↔ Person ↔ WebSite for entity grounding
 * (improves AEO / GEO recognition by AI engines and search).
 */
export function installLocalBusinessSchema() {
  const orgId = `${SITE.url}/#organization`;
  const placeId = `${SITE.url}/#localbusiness`;
  const personId = `${SITE.url}/#bella`;
  const siteId = `${SITE.url}/#website`;
  const logoId = `${SITE.url}/#logo`;
  const contactBookingId = `${SITE.url}/#contact-booking`;
  const contactCustomerId = `${SITE.url}/#contact-customer`;

  // Canonical Service nodes — allows AI assistants to enumerate offerings without hallucinating.
  const serviceCatalog = [
    {
      id: `${SITE.url}/#service-massage-60`,
      name: "60 Minute Therapeutic Massage",
      description: "Full-body therapeutic massage tailored to tension, posture, and recovery goals. One-hour session in the downtown Sacramento studio.",
      serviceType: "Massage Therapy",
      price: "90",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-massage-90`,
      name: "90 Minute Therapeutic Massage",
      description: "Extended therapeutic massage with deeper focus on chronic tension areas. Ninety-minute session in the downtown Sacramento studio.",
      serviceType: "Massage Therapy",
      price: "135",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-massage-120`,
      name: "The Long Restoration — 120 Minute Massage",
      description: "Two full hours of unhurried, head-to-toe therapeutic bodywork for deep restoration.",
      serviceType: "Massage Therapy",
      price: "180",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-massage-trio`,
      name: "Massage Trio Package",
      description: "Three prepaid 60-minute therapeutic massage sessions, redeemable over multiple visits. Ideal for ongoing self-care.",
      serviceType: "Massage Package",
      price: "240",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-facial-signature`,
      name: "The Signature Glow Facial",
      description: "A 60-minute custom facial calibrated to your skin — cleanse, exfoliate, extractions as needed, and finishing mask and massage.",
      serviceType: "Facial",
      price: "100",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-facial-grand`,
      name: "The Grand Ritual Facial",
      description: "Ninety minutes of extended skin care — deep cleanse, extended facial massage, décolleté work, and treatment mask.",
      serviceType: "Facial",
      price: "145",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-renewal-peel`,
      name: "The Renewal Peel",
      description: "A professional 60-minute chemical peel tailored to your skin's tolerance and goals.",
      serviceType: "Chemical Peel",
      price: "110",
      url: `${SITE.url}/#services`,
    },
    {
      id: `${SITE.url}/#service-velvet-dermaplane`,
      name: "The Velvet Dermaplane",
      description: "A 90-minute dermaplaning treatment for luminous, camera-ready skin. Finished with a soothing mask and SPF.",
      serviceType: "Facial",
      price: "110",
      url: `${SITE.url}/#services`,
    },
  ];

  const serviceNodes = serviceCatalog.map((s) => ({
    "@type": "Service",
    "@id": s.id,
    name: s.name,
    description: s.description,
    serviceType: s.serviceType,
    url: s.url,
    provider: { "@id": placeId },
    areaServed: [
      { "@type": "City", name: "Sacramento" },
      { "@type": "Place", name: "Midtown Sacramento" },
      { "@type": "Place", name: "Downtown Sacramento" },
    ],
    ...(s.price && {
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: s.price,
        availability: "https://schema.org/InStock",
        url: `${SITE.url}/#booking`,
      },
    }),
  }));

  setJsonLd("ld-local-business", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: SITE.name,
        url: SITE.url,
        logo: { "@id": logoId },
        sameAs: [],
        contactPoint: [
          { "@id": contactBookingId },
          { "@id": contactCustomerId },
        ],
      },
      {
        "@type": "ImageObject",
        "@id": logoId,
        url: SITE.defaultImage,
        contentUrl: SITE.defaultImage,
        caption: `${SITE.name} — logo and brand image`,
        creator: { "@id": personId },
        copyrightHolder: { "@id": orgId },
        creditText: SITE.name,
        license: `${SITE.url}/`,
        acquireLicensePage: `${SITE.url}/#contact`,
        copyrightNotice: `© ${new Date().getFullYear()} ${SITE.name}. All rights reserved.`,
      },
      {
        "@type": "ContactPoint",
        "@id": contactBookingId,
        contactType: "reservations",
        telephone: SITE.phone,
        email: SITE.email,
        url: `${SITE.url}/#booking`,
        areaServed: "US-CA",
        availableLanguage: ["English"],
      },
      {
        "@type": "ContactPoint",
        "@id": contactCustomerId,
        contactType: "customer support",
        telephone: SITE.phone,
        email: SITE.email,
        url: `${SITE.url}/#contact`,
        areaServed: "US-CA",
        availableLanguage: ["English"],
      },
      {
        "@type": "WebSite",
        "@id": siteId,
        url: SITE.url,
        name: SITE.name,
        publisher: { "@id": orgId },
        inLanguage: "en-US",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE.url}/blog?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Person",
        "@id": personId,
        name: "Bella",
        jobTitle: "Licensed Esthetician",
        worksFor: { "@id": orgId },
        knowsAbout: [
          "Therapeutic massage",
          "Custom facials",
          "Skin care",
          "Sacramento esthetics",
        ],
        hasCredential: {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "license",
          name: "California Esthetics License",
          recognizedBy: {
            "@type": "Organization",
            name: "California Board of Barbering and Cosmetology",
            url: "https://www.barbercosmo.ca.gov/",
          },
        },
      },
      {
        "@type": ["LocalBusiness", "BeautySalon", "HealthAndBeautyBusiness"],
        "@id": placeId,
        name: SITE.name,
        alternateName: ["Beauty and Balance by Bella", "Beauty & Balance Sacramento"],
        image: { "@id": logoId },
        logo: { "@id": logoId },
        url: SITE.url,
        telephone: SITE.phone,
        email: SITE.email,
        priceRange: "$$",
        parentOrganization: { "@id": orgId },
        founder: { "@id": personId },
        employee: { "@id": personId },
        contactPoint: [
          { "@id": contactBookingId },
          { "@id": contactCustomerId },
        ],
        address: {
          "@type": "PostalAddress",
          addressLocality: "Sacramento",
          addressRegion: "CA",
          addressCountry: "US",
        },
        areaServed: [
          { "@type": "City", name: "Sacramento" },
          { "@type": "Place", name: "Midtown Sacramento" },
          { "@type": "Place", name: "East Sacramento" },
          { "@type": "Place", name: "Downtown Sacramento" },
          { "@type": "Place", name: "Land Park" },
          { "@type": "Place", name: "Curtis Park" },
          { "@type": "Place", name: "Oak Park" },
          { "@type": "City", name: "Davis" },
          { "@type": "City", name: "Roseville" },
          { "@type": "City", name: "Folsom" },
          { "@type": "City", name: "Carmichael" },
          { "@type": "City", name: "Citrus Heights" },
          { "@type": "City", name: "El Dorado Hills" },
          { "@type": "AdministrativeArea", name: "Sacramento County" },
        ],
        geo: {
          "@type": "GeoCoordinates",
          latitude: 38.5816,
          longitude: -121.4944,
        },
        openingHoursSpecification: [
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Monday", opens: "10:00", closes: "17:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "11:00", closes: "14:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Wednesday", opens: "11:00", closes: "14:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "11:00", closes: "14:00" },
        ],
        currenciesAccepted: "USD",
        paymentAccepted: "Cash, Credit Card",
        slogan: "Self-care, softly rendered.",
        knowsAbout: [
          "Sacramento esthetician services",
          "Therapeutic massage",
          "Custom facials",
          "Licensed esthetics in California",
          "Skin care education",
        ],
        description:
          "Beauty & Balance by Bella is a licensed esthetician studio in downtown Sacramento, California offering therapeutic massage and custom facials. By appointment only.",
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Beauty & Balance by Bella — Services",
          itemListElement: serviceCatalog.map((s) => ({
            "@type": "Offer",
            ...(s.price && { priceCurrency: "USD", price: s.price }),
            url: `${SITE.url}/#booking`,
            itemOffered: { "@id": s.id },
          })),
        },
        makesOffer: [
          {
            "@type": "Offer", priceCurrency: "USD", price: "90",
            itemOffered: { "@id": `${SITE.url}/#service-massage-60` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "135",
            itemOffered: { "@id": `${SITE.url}/#service-massage-90` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "180",
            itemOffered: { "@id": `${SITE.url}/#service-massage-120` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "240",
            itemOffered: { "@id": `${SITE.url}/#service-massage-trio` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "100",
            itemOffered: { "@id": `${SITE.url}/#service-facial-signature` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "145",
            itemOffered: { "@id": `${SITE.url}/#service-facial-grand` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "110",
            itemOffered: { "@id": `${SITE.url}/#service-renewal-peel` },
          },
          {
            "@type": "Offer", priceCurrency: "USD", price: "110",
            itemOffered: { "@id": `${SITE.url}/#service-velvet-dermaplane` },
          },
        ],
        potentialAction: {
          "@type": "ReserveAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE.url}/#booking`,
            actionPlatform: [
              "http://schema.org/DesktopWebPlatform",
              "http://schema.org/MobileWebPlatform",
            ],
          },
          result: {
            "@type": "Reservation",
            name: "Appointment at Beauty & Balance by Bella",
          },
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "5.0",
          reviewCount: "3",
          bestRating: "5",
          worstRating: "1",
        },
        review: [
          { "@type": "Review", reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" }, author: { "@type": "Person", name: "Linda S." }, reviewBody: "Beauty & Balance by Bella's facials have been a game-changer for my skin. I feel more confident and radiant than ever before." },
          { "@type": "Review", reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" }, author: { "@type": "Person", name: "David C." }, reviewBody: "I've experienced various beauty treatments, but Bella's rejuvenating facials have truly made a visible difference in reducing my fine lines." },
          { "@type": "Review", reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" }, author: { "@type": "Person", name: "Emily J." }, reviewBody: "Therapeutic massages at Beauty & Balance have transformed my well-being, making me feel rejuvenated and balanced." },
        ],
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "h2", "[data-testid='faq-section']"],
        },
      },
      ...serviceNodes,
    ],
  });
}

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["[data-testid='faq-section']"],
    },
  };
}

export function buildBlogPostingSchema(post) {
  // Strip HTML for articleBody + word count; safely no-op if content_html missing.
  const rawHtml = post.content_html || "";
  const plainText = rawHtml
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const wordCount = plainText ? plainText.split(" ").length : undefined;

  const about = (post.tags || []).slice(0, 6).map((t) => ({ "@type": "Thing", name: t }));
  const mentions = (post.keywords || []).slice(0, 8).map((k) => ({ "@type": "Thing", name: k }));

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE.url}/blog/${post.slug}#article`,
    headline: post.title,
    name: post.title,
    description: post.meta_description || post.excerpt,
    image: post.hero_image,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    inLanguage: "en-US",
    isFamilyFriendly: true,
    articleSection: (post.tags && post.tags[0]) || "Journal",
    ...(wordCount && { wordCount }),
    ...(plainText && { articleBody: plainText.slice(0, 5000) }),
    ...(about.length && { about }),
    ...(mentions.length && { mentions }),
    author: {
      "@type": "Person",
      "@id": `${SITE.url}/#bella`,
      name: post.author || "Bella",
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE.url}/#organization`,
      name: SITE.name,
      logo: { "@type": "ImageObject", url: SITE.defaultImage },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE.url}/blog/${post.slug}`,
    },
    url: `${SITE.url}/blog/${post.slug}`,
    keywords: (post.keywords || post.tags || []).join(", "),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2", "[data-testid='post-excerpt']"],
    },
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE.url}${it.url}`,
    })),
  };
}
